import React, { useState, useEffect } from 'react';
import { X, Heart, MessageCircle, Video, CreditCard, Bell, Check } from 'lucide-react';

const ToastNotification = ({ notification, onClose, onAction }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Show animation
    setTimeout(() => setIsVisible(true), 100);
    
    // Auto-hide after 5 seconds
    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleAction = () => {
    if (onAction) {
      onAction(notification);
    }
    handleClose();
  };

  const getIcon = () => {
    const iconClass = "h-6 w-6";
    
    if (notification.type.includes('match')) {
      return <Heart className={`${iconClass} text-red-500`} />;
    } else if (notification.type.includes('message')) {
      return <MessageCircle className={`${iconClass} text-blue-500`} />;
    } else if (notification.type.includes('video')) {
      return <Video className={`${iconClass} text-purple-500`} />;
    } else if (notification.type.includes('payment')) {
      return <CreditCard className={`${iconClass} text-green-500`} />;
    }
    
    return <Bell className={`${iconClass} text-gray-500`} />;
  };

  const getPriorityStyle = () => {
    switch (notification.priority) {
      case 'critical':
        return 'bg-red-500 border-red-600';
      case 'high':
        return 'bg-orange-500 border-orange-600';
      case 'medium':
        return 'bg-blue-500 border-blue-600';
      default:
        return 'bg-gray-500 border-gray-600';
    }
  };

  return (
    <div
      className={`fixed top-4 right-4 max-w-sm w-full z-50 transform transition-all duration-300 ease-in-out ${
        isVisible && !isExiting 
          ? 'translate-x-0 opacity-100 scale-100' 
          : 'translate-x-full opacity-0 scale-95'
      }`}
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Priority indicator */}
        <div className={`h-1 ${getPriorityStyle()}`}></div>
        
        <div className="p-4">
          <div className="flex items-start space-x-3">
            {/* Icon */}
            <div className="flex-shrink-0 mt-1">
              {getIcon()}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-2">
                  <h4 className="text-sm font-semibold text-gray-900 leading-tight">
                    {notification.title}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1 leading-tight">
                    {notification.message}
                  </p>
                </div>
                
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Action button */}
              {notification.action_label && (
                <div className="mt-3">
                  <button
                    onClick={handleAction}
                    className="inline-flex items-center px-3 py-1 bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium rounded-full transition-colors"
                  >
                    {notification.action_label}
                  </button>
                </div>
              )}
              
              {/* Timestamp */}
              <div className="mt-2">
                <span className="text-xs text-gray-500">
                  {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Toast Notification Manager Component
export const ToastManager = () => {
  const [toasts, setToasts] = useState([]);

  const addToast = (notification) => {
    const toastId = Date.now().toString();
    const toastNotification = { ...notification, id: toastId };
    
    setToasts(prev => [...prev, toastNotification]);
  };

  const removeToast = (toastId) => {
    setToasts(prev => prev.filter(toast => toast.id !== toastId));
  };

  const handleToastAction = (notification) => {
    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };

  // Expose addToast function globally for WebSocket integration
  useEffect(() => {
    window.showNotificationToast = addToast;
    
    return () => {
      window.showNotificationToast = null;
    };
  }, []);

  return (
    <div className="fixed top-0 right-0 z-50 pointer-events-none">
      <div className="flex flex-col space-y-2 p-4 pointer-events-auto">
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            style={{ zIndex: 1000 - index }} // Stack toasts properly
          >
            <ToastNotification
              notification={toast}
              onClose={() => removeToast(toast.id)}
              onAction={handleToastAction}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ToastNotification;